import { BoxV2Icon } from '@jouzen/ecom-components/icons/BoxV2Icon';
import { CreditCardIcon } from '@jouzen/ecom-components/icons/CreditCardIcon';
import { ExtendLogo } from '@jouzen/ecom-components/icons/ExtendLogo';
import { RingOutlineIcon } from '@jouzen/ecom-components/icons/RingOutlineIcon';
import { ThumbsUpIcon } from '@jouzen/ecom-components/icons/ThumbsUpIcon';
import { Price } from '@jouzen/ecom-components/v3/Price';
import type { ProductSelectProps } from '@jouzen/ecom-components/v3/ProductSelect';
import { CardSizeSelections } from '@jouzen/ecom-components/v3/ProductSelectCards';
import type { RingSizeData } from '@jouzen/ecom-components/v3/types/ringSizeData';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { Accordion, AccordionItem } from '@jouzen/ecom-components/v4/Accordion';
import { useGateValue } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';

import { EventType } from '@/analytics/types';
import type {
  BuildHardwareOrderBaseProps,
  Order,
} from '@/app/[locale]/store/_components/HardwarePDP/_types/order';
import { getPriceAndDiscount } from '@/app/[locale]/store/_components/HardwarePDP/_utils/getPriceAndDiscount';
import Image from '@/app/components/Image';
import RichText from '@/app/components/RichText';
import Video from '@/app/components/Video/Video';
import SizeSelectionEnum from '@/app/enums/SizeSelectionEnum';
import WarrantySelectionEnum from '@/app/enums/WarrantySelectionEnum';
import type { ListProduct } from '@/queries/Products';

import RingInventoryMessage from '../RingInventoryMessage';

export interface UseRingProductSelectDataProps
  extends Pick<
    BuildHardwareOrderBaseProps,
    | 'monthlyMembership'
    | 'onRadioChange'
    | 'onSizeChange'
    | 'order'
    | 'ringSizesData'
    | 'threeYearWarranty'
    | 'twoYearWarranty'
    | 'yearlyMembership'
  > {
  currencyCode: string;
  routePrefix: string;
}

const getAddOnPrices = ({
  monthlyMembership,
  yearlyMembership,
  twoYearWarranty,
  threeYearWarranty,
}: {
  monthlyMembership: ListProduct | undefined;
  yearlyMembership: ListProduct | undefined;
  twoYearWarranty: ListProduct | undefined;
  threeYearWarranty: ListProduct | undefined;
}) => {
  const { price: membershipMonthlyPrice } =
    getPriceAndDiscount(monthlyMembership);
  const { price: membershipYearlyPrice } =
    getPriceAndDiscount(yearlyMembership);
  const { price: warrantyTwoYearPrice } = getPriceAndDiscount(twoYearWarranty);
  const { price: warrantyThreeYearPrice } =
    getPriceAndDiscount(threeYearWarranty);

  return {
    membershipMonthlyPrice,
    membershipYearlyPrice,
    warrantyTwoYearPrice,
    warrantyThreeYearPrice,
  };
};

const getSelections = ({
  order,
  ringSizesData,
}: {
  order: Order;
  ringSizesData: RingSizeData[] | undefined;
}) => {
  // set initial values for size, membership, and warranty
  const sizeValue = order.size.value;
  const selectedMembership = order.membership.value;
  const selectedWarranty = order.warranty.value;
  const sizeRadioInitialValue =
    sizeValue === SizeSelectionEnum.SizingKit.toString()
      ? SizeSelectionEnum.SizingKit
      : SizeSelectionEnum.ChooseSize;
  const selectedSize =
    sizeValue !== SizeSelectionEnum.SizingKit.toString()
      ? { initialSelectedSize: Number(sizeValue) }
      : {};

  // Filter out size later option
  const chooseSizeOptions =
    ringSizesData?.filter(({ size }) => !isNaN(Number(size))) ?? [];

  return {
    sizeRadioInitialValue,
    selectedSize,
    selectedMembership,
    selectedWarranty,
    chooseSizeOptions,
  };
};

const useRingProductSelectData = ({
  currencyCode,
  monthlyMembership,
  yearlyMembership,
  twoYearWarranty,
  threeYearWarranty,
  onRadioChange,
  onSizeChange,
  order,
  ringSizesData,
  routePrefix,
}: UseRingProductSelectDataProps): ProductSelectProps[] => {
  const t = useTranslations();
  const isWarrantyAvailable = useGateValue('extended_warranty');

  const {
    sizeRadioInitialValue,
    selectedSize,
    selectedWarranty,
    chooseSizeOptions,
  } = getSelections({
    order,
    ringSizesData,
  });

  const { warrantyTwoYearPrice, warrantyThreeYearPrice } = getAddOnPrices({
    monthlyMembership,
    yearlyMembership,
    twoYearWarranty,
    threeYearWarranty,
  });

  const getInfoClick = (section: string) => {
    return () => {
      void window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: 'info',
        location: section,
      });
    };
  };

  if (!ringSizesData) {
    return [];
  }

  const sizingSelectProps: ProductSelectProps = {
    cardSelectorsData: [
      {
        id: SizeSelectionEnum.SizingKit,
        title: t('free_sizing_kit'),
      },
      {
        additionalContent: (
          <CardSizeSelections
            disableWhenOutOfStock={false}
            label={t('pdp_step_know_size')}
            inventoryMessage={
              <RingInventoryMessage
                routePrefix={routePrefix}
                stockStatus={order.product.stockStatus}
                selectedRingSize={order.size.value}
              />
            }
            message={t('ring_pdp_2025_size_selection_drawer_footnote')}
            onChange={onSizeChange}
            ringSizesData={chooseSizeOptions}
            {...selectedSize}
          />
        ),
        childIds: chooseSizeOptions.map(({ size }) => size.toString()),
        description: t('ring_pdp_2025_size_option_description'),
        id: SizeSelectionEnum.ChooseSize,
        showContentOnExpand: true,
        title: t('ring_pdp_2025_size_option_select_size'),
      },
    ],
    description: t('ring_pdp_2025_size_selection_subtitle'),
    infoModal: {
      footnote: <RichText messageKey="ring_pdp_2025_sizing_modal_footnote_1" />,
      media: {
        video: (
          <Video
            className="size-full object-cover"
            contentTitle="or4_sizing_video"
            location="sizing-hero"
            localizedSrc="sizing_gen4_sizing_video_src"
            playVideo={false}
            preload="metadata"
            type="video/mp4"
            posterSrc="sizing/video-poster.png"
          />
        ),
      },
      rightColumnContent: (
        <div className="lg:pl-6">
          <Accordion
            className="mb-4 w-full flex-1 shrink grow gap-3 md:mb-0"
            headerClassName="pr-4 text-base font-bold"
          >
            <AccordionItem
              header={t('ring_pdp_2025_sizing_modal_step_1_title')}
              icon={<CreditCardIcon />}
            >
              {t('ring_pdp_2025_sizing_modal_step_1_copy')}
            </AccordionItem>
            <AccordionItem
              header={t('ring_pdp_2025_sizing_modal_step_2_title')}
              icon={<BoxV2Icon />}
            >
              {t('ring_pdp_2025_sizing_modal_step_2_copy')}
            </AccordionItem>
            <AccordionItem
              header={t('ring_pdp_2025_sizing_modal_step_3_title')}
              icon={<ThumbsUpIcon />}
            >
              {t('ring_pdp_2025_sizing_modal_step_3_copy')}
            </AccordionItem>
            <AccordionItem
              header={t('ring_pdp_2025_sizing_modal_step_4_title')}
              icon={<RingOutlineIcon />}
            >
              {t('ring_pdp_2025_sizing_modal_step_4_copy')}
            </AccordionItem>
          </Accordion>
        </div>
      ),
      title: <RichText messageKey="ring_pdp_2025_sizing_modal_title" />,
      triggerButton: {
        label: t('ring_pdp_2025_size_modal_label'),
        onClick: getInfoClick('sizing'),
      },
    },
    initialSelection: sizeRadioInitialValue,
    onChange: (value: string) => {
      const ctaValues: Record<SizeSelectionEnum, string> = {
        [SizeSelectionEnum.ChooseSize]: 'select your size',
        [SizeSelectionEnum.SizingKit]: 'sizing kit',
      };
      void window.ouraAnalytics.track(EventType.ModuleClicked, {
        cta: ctaValues[value as SizeSelectionEnum],
        location: 'pdp',
        step: 2,
      });
      onRadioChange(value);
    },
    title: t('pdp_size'),
  };

  const warrantySelectProps: ProductSelectProps | null = isWarrantyAvailable
    ? {
        cardSelectorsData: [
          {
            description: t('ring_pdp_2025_warranty_1_year_subtitle'),
            id: WarrantySelectionEnum.OneYear,
            title: t('ring_pdp_2025_warranty_option_1_year'),
          },
          {
            description: (
              <>
                {warrantyTwoYearPrice ? (
                  <Price
                    currencyCode={currencyCode}
                    price={warrantyTwoYearPrice}
                  />
                ) : null}
              </>
            ),
            id: WarrantySelectionEnum.TwoYear,
            title: t('ring_pdp_2025_warranty_option_2_year'),
          },
          {
            description: (
              <>
                {warrantyThreeYearPrice ? (
                  <Price
                    currencyCode={currencyCode}
                    price={warrantyThreeYearPrice}
                  />
                ) : null}
                <span className="block">
                  {t('ring_pdp_2025_warranty_3_year_subtitle')}
                </span>
              </>
            ),
            id: WarrantySelectionEnum.ThreeYear,
            title: t('ring_pdp_2025_warranty_option_3_year'),
          },
        ],
        description: t('ring_pdp_2025_warranty_selection_subtitle'),
        infoModal: {
          eyebrow: (
            <Typography className="hidden text-sm font-normal text-gray-400 italic md:block">
              <RichText
                messageKey="ring_pdp_2025_warranty_modal_eyebrow"
                tValues={{
                  ExtendLogo: () => (
                    <ExtendLogo
                      className="ml-1.5 inline-block h-4 w-12 -translate-y-px"
                      title="Extend"
                    />
                  ),
                }}
              />
            </Typography>
          ),
          footnote: (
            <RichText messageKey="ring_pdp_2025_warranty_modal_footnote" />
          ),
          media: {
            image: (
              <Image
                className="size-full object-cover"
                localizedAlt="woman_close_up_image_alt"
                height="217"
                src="hardware-pdp/modal/warranty.jpg"
                width="386"
              />
            ),
          },
          rightColumnContent: (
            <div className="lg:pt-5">
              <Typography className="mb-3 text-base font-bold md:text-xl md:font-normal">
                <RichText messageKey="ring_pdp_2025_warranty_modal_2_and_3_year_heading" />
              </Typography>
              <ul className="list-outside list-disc space-y-0.5 pl-6 text-sm font-normal">
                <RichText messageKey="ring_pdp_2025_warranty_modal_2_and_3_year_copy" />
              </ul>
            </div>
          ),
          title: <RichText messageKey="ring_pdp_2025_warranty_modal_title" />,
          triggerButton: {
            label: t('ring_pdp_2025_warranty_modal_label'),
            onClick: getInfoClick('warranty'),
          },
        },
        initialSelection: selectedWarranty,
        onChange: (value: string) => {
          const ctaValues: Record<WarrantySelectionEnum, string> = {
            [WarrantySelectionEnum.OneYear]: '1-year protection plan',
            [WarrantySelectionEnum.TwoYear]: 'upgrade to 2-year',
            [WarrantySelectionEnum.ThreeYear]: 'upgrade to 3-year',
          };
          void window.ouraAnalytics.track(EventType.ModuleClicked, {
            cta: ctaValues[value as WarrantySelectionEnum],
            location: 'pdp',
            step: 4,
          });
          onRadioChange(value);
        },
        title: t('warranty'),
      }
    : null;

  return [sizingSelectProps, warrantySelectProps].filter(
    (selectProps) => selectProps !== null,
  );
};

export default useRingProductSelectData;
